/**
 * Deriva le colonne legacy `price_per_day` / `price_half_day` di `seasonal_prices`
 * dalle FASCE prezzate (`slots`). La fascia è la sorgente autoriale unica (wizard prezzi);
 * le colonne restano una cache derivata che il motore booking già legge
 * (lib/seasonal-pricing.ts → booking-utils/booking-calculations, markup, SQL, headline).
 *
 * Regola:
 *  - price_per_day  = fascia "Giornata" (label/id contiene "giornat"); in mancanza, la fascia
 *    prezzata più cara (una giornata intera è tipicamente la più costosa).
 *  - price_half_day = fascia mezza (Mattina/Pomeriggio) più economica; null se non ci sono mezze.
 *
 * Ritorna `null` su un campo NON derivabile (nessuna fascia prezzata / nessuna mezza):
 * il chiamante in quel caso NON deve sovrascrivere la colonna esistente.
 *
 * NB: se Mattina e Pomeriggio hanno prezzi diversi, il motore attuale conosce UN solo
 * prezzo mezza-giornata → qui si prende il minimo (floor "da €"). Il vero prezzo distinto
 * per-fascia al booking è un passo successivo (richiede modifica al motore).
 */

import type { SeasonalSlot, PriceMode } from '@/lib/excursion-seasonal-slots';
import { DAY_PART_SPLIT_HOUR, DAY_PART_SPLIT_HHMM } from '@/lib/day-part';

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Prezzo della fascia per la modalità (per_person/per_group → campo dedicato,
 *  fallback al `price` LEGACY condiviso). Boats/undefined → `price`. */
const slotPrice = (s: SeasonalSlot, mode?: PriceMode): number => {
  const specific = mode === 'per_person' ? s.price_per_person : mode === 'per_group' ? s.price_group : undefined;
  return num(specific) > 0 ? num(specific) : num(s?.price);
};

// "Giornata intera" = fascia full-day. ATTENZIONE: "Mezza Giornata Mattina/Pomeriggio"
// contiene "giornat" ma è una MEZZA → escludere label/id con "mezz"/"half", altrimenti
// le mezze vengono classificate full-day e price_half_day resta null (mezza sparisce dal wizard).
const isHalfLabel = (v: string): boolean => /mezz|half/i.test(v);
export const isFullDay = (s: SeasonalSlot): boolean => {
  const label = String(s.label || '');
  const id = String((s as { id?: string }).id || '');
  if (isHalfLabel(label) || isHalfLabel(id)) return false;
  return /giornat/i.test(label) || /giornat/i.test(id);
};

export interface DerivedSeasonColumns {
  price_per_day: number | null;
  /** Mezza più economica (mattina+pomeriggio insieme) — floor "da €" per il display. */
  price_half_day: number | null;
  /** Mezza-MATTINA: min delle fasce con start_time < cutoff. null se assente. */
  price_half_morning: number | null;
  /** Mezza-POMERIGGIO: min delle fasce con start_time >= cutoff. null se assente. */
  price_half_afternoon: number | null;
}

/** 'HH:MM' → minuti dalla mezzanotte; NaN/assente → null. */
const toMin = (hhmm: unknown): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm ?? ''));
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

/**
 * Una stagione è "prezzata" (→ prenotabile) per la MODALITÀ di noleggio della barca:
 *  - daily/hourly → almeno una fascia con prezzo (slots) o price_per_day > 0
 *  - weekly       → price_per_week > 0
 *  - monthly      → price_per_month > 0
 * Modello solo-stagionale: niente fallback al prezzo base barca.
 */
export function isSeasonPriced(
  row: { price_per_day?: unknown; price_per_week?: unknown; price_per_month?: unknown; slots?: unknown },
  mode: string = 'daily',
): boolean {
  if (mode === 'weekly') return num(row?.price_per_week) > 0;
  if (mode === 'monthly') return num(row?.price_per_month) > 0;
  return deriveSeasonColumns(row?.slots).price_per_day != null || num(row?.price_per_day) > 0;
}

export function deriveSeasonColumns(
  slots: unknown,
  mode?: PriceMode,
  cutoff: string = DAY_PART_SPLIT_HHMM,
): DerivedSeasonColumns {
  const arr = Array.isArray(slots) ? (slots as SeasonalSlot[]) : [];
  const priced = arr.filter((s) => slotPrice(s, mode) > 0);
  if (priced.length === 0) {
    return { price_per_day: null, price_half_day: null, price_half_morning: null, price_half_afternoon: null };
  }

  const full = priced.filter(isFullDay);
  // Le MEZZE sono solo fasce DAILY (Mattina/Pomeriggio). Le fasce a ORE
  // (rental_mode='hourly', es. "2h") NON sono mezze giornate: vanno escluse o
  // il loro prezzo (tipicamente il più basso) verrebbe spacciato per "mezza".
  const half = priced.filter((s) => !isFullDay(s) && String(s.rental_mode || 'daily') !== 'hourly');

  const price_per_day = full.length
    ? Math.max(...full.map((s) => slotPrice(s, mode)))
    : Math.max(...priced.map((s) => slotPrice(s, mode)));
  const price_half_day = half.length ? Math.min(...half.map((s) => slotPrice(s, mode))) : null;

  // Split per lato-cutoff (stessa semantica di priceForSpan): mattina start<cutoff,
  // pomeriggio start>=cutoff. Fasce senza start_time → in nessun lato (restano nel floor
  // price_half_day). Prezzo lato = fascia più economica di quel lato.
  const cutMin = toMin(cutoff) ?? DAY_PART_SPLIT_HOUR * 60;
  const minOf = (xs: SeasonalSlot[]) => (xs.length ? Math.min(...xs.map((s) => slotPrice(s, mode))) : null);
  const morning = half.filter((s) => { const t = toMin(s.start_time); return t != null && t < cutMin; });
  const afternoon = half.filter((s) => { const t = toMin(s.start_time); return t != null && t >= cutMin; });

  return {
    price_per_day,
    price_half_day,
    price_half_morning: minOf(morning),
    price_half_afternoon: minOf(afternoon),
  };
}
